import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'

import CompassMark from './ui/CompassMark'

const PRODUCT_LINKS = [
  { label: 'AI Tool Finder', to: '/ai-tool-finder' },
  { label: 'Catalog', to: '/tools' },
  { label: 'Collections', to: '/collections' },
  { label: 'Community Stacks', to: '/stacks' },
  { label: 'LLM Cost Calculator', to: '/model-comparison' },
  { label: 'Submit a tool', to: '/submit' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'FAQ & Support', to: '/help' },
  { label: 'Syllabus Parser', to: '/syllabus-parser' },
  { label: 'Student Discounts', to: '/student-discounts' },
]

const GUIDES_LINKS = [
  { label: 'Best for students', to: '/best-ai-tools-for-students' },
  { label: 'Best free tools', to: '/best-free-ai-tools' },
  { label: 'Best coding tools', to: '/best-coding-tools-for-students' },
  { label: 'Best productivity tools', to: '/best-ai-productivity-tools' },
]

const ABOUT_LINKS = [
  { label: 'About Us', to: '/about' },
  { label: 'Support AI Compass', to: '/support' },
  { label: 'Contact', to: '/contact' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Refund Policy', to: '/refunds' },
]

const HOW_TO_LINKS = [
  { label: 'Claim GitHub Student Pack', to: '/guides/github-student-pack' },
  { label: 'Claim Notion Education Plan', to: '/guides/notion-student-premium' },
  { label: 'Claim JetBrains License', to: '/guides/jetbrains-student-license' },
  { label: 'All guides', to: '/guides' },
]

function LinkedinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0Z" />
    </svg>
  )
}

function BackToTopButton() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px
      if (window.scrollY > 300) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!isVisible) return null

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      data-float
      className="fixed bottom-[5.25rem] right-5 z-40 flex items-center gap-2 rounded-full border border-line bg-bg-elev/80 px-4 py-2.5 text-sm font-semibold text-ink shadow-lg backdrop-blur-md transition-all hover:scale-105 hover:border-accent/40 hover:bg-bg-elev/90 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent group"
      aria-label="Back to Top"
    >
      <span>Back to Top</span>
      <ArrowUp className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
    </button>
  )
}

export default function Footer() {
  return (
    <footer className="border-t border-line dark:border-line-strong bg-bg py-10 md:py-12 relative">
      <div className="mx-auto max-w-6xl px-5">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <CompassMark size={24} />
              <span className="text-base font-semibold text-ink">AI Compass</span>
            </div>
            <p className="mt-3 max-w-[32ch] text-sm leading-relaxed text-muted font-sans">
              A hand-checked AI tool directory for students. Run by{' '}
              <a
                href="https://medhansh.vercel.app"
                target="_blank"
                rel="noopener"
                className="text-ink-2 underline underline-offset-4 transition-colors hover:text-accent-ink"
              >
                Medhansh Pratap Singh
              </a>{' '}
              in Bengaluru, India.
            </p>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">
              PRODUCT
            </h3>
            <ul className="space-y-2">
              {PRODUCT_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-ink-2 transition-colors hover:text-accent-ink"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">
              GUIDES
            </h3>
            <ul className="space-y-2">
              {GUIDES_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-ink-2 transition-colors hover:text-accent-ink"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">
              HOW TO
            </h3>
            <ul className="space-y-2">
              {HOW_TO_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-ink-2 transition-colors hover:text-accent-ink"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">
              ABOUT
            </h3>
            <ul className="space-y-2">
              {ABOUT_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-ink-2 transition-colors hover:text-accent-ink"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-8 border-t border-line pt-5 text-xs leading-relaxed text-muted">
          <strong className="font-semibold text-ink-2">How this site is paid for.</strong>{' '}
          Paid listings and sponsored placements are always labelled &ldquo;Sponsored&rdquo;. Some pages
          show Google ads. Some links to tools are affiliate links, which may earn a small commission at
          no cost to you. None of these affect ratings, reviews or editor&apos;s picks.
        </p>

        <p className="mt-3 text-xs leading-relaxed text-muted">
          <strong className="font-semibold text-ink-2">Also from the maker.</strong>{' '}
          <a
            href="/go/manila?from=footer"
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            className="text-ink-2 underline underline-offset-4 transition-colors hover:text-accent-ink"
          >
            Manilla
          </a>{' '}
          collects files and answers from anyone with one link. It&apos;s my own product, not a paid
          listing.
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <div className="flex items-center gap-3">
            <span>© 2026 AI Compass · ai-compass.in</span>
            <span className="text-line">|</span>
            <a
              href="https://www.linkedin.com/company/117624209/"
              target="_blank"
              rel="noreferrer"
              className="text-muted hover:text-accent transition-colors flex items-center"
              aria-label="LinkedIn"
            >
              <LinkedinIcon className="h-4 w-4" />
            </a>
          </div>
          <span className="font-mono text-xs">Made with care, not scrapers.</span>
        </div>
      </div>
      <BackToTopButton />
    </footer>
  )
}
